import type { Project } from "@tudigo-monorepo/core-tudigo-api-models";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { useUserBusiness } from "@tudigo-monorepo/tudigo-session";
import { useGetProjectsQuery } from "@tudigo-monorepo/web-tudigo-api-client";
import { ComparatorProvider } from "@tudigo-monorepo/web-tudigo-comparator";

import { AppointmentCardNew } from "../../shared-external/components/appointment-card-new";
import {
  SearchProjectsHeader,
  SearchProjectsHeaderContent,
  SearchProjectsHeaderTitle,
} from "../shared-internal/components/search-projects/search-projects-header";
import { SearchProjectsRefinements } from "../shared-internal/components/search-projects/search-projects-refinements";
import { SearchProjectsRefinementsMenu } from "../shared-internal/components/search-projects/search-projects-refinements-menu";
import { SearchProjectsRefinementsMenuTrigger } from "../shared-internal/components/search-projects/search-projects-refinements-menu-trigger";
import { SearchProjectsProvider } from "../shared-internal/state/search-projects.context";
import { ProjectsComparatorActions } from "./components/projects-comparator/projects-comparator-actions";
import { ProjectsComparatorModal } from "./components/projects-comparator/projects-comparator-modal/projects-comparator-modal";
import { ProjectsComparatorSelectionBanner } from "./components/projects-comparator/projects-comparator-selection-banner";
import { StartedProjectsContent } from "./components/started-projects-content";
import { TrackRecordBlock } from "./components/track-record-block";
import { useProjectsComparatorTracking } from "./hooks/use-projects-comparator-tracking";
import { useSearchProjectsRefinementsCustomerTracking } from "./hooks/use-search-projects-refinements-customer-tracking";

export function SearchStartedProjectsPage() {
  const { t } = useTranslation();
  const { trackRefinement } = useSearchProjectsRefinementsCustomerTracking();
  const { averageInvestmentAmountCapacityInCents } = useUserBusiness();

  const comparatorSimulatorAmount =
    averageInvestmentAmountCapacityInCents / 100;

  const trackComparisonDone = useProjectsComparatorTracking();

  return (
    <SearchProjectsProvider
      status="started"
      refinements={["instrument", "tax_exemption", "risk"]}
    >
      <ComparatorProvider
        maxSelection={6}
        defaultSimulatorAmount={comparatorSimulatorAmount}
        compareFn={(a: Project, b: Project) => a.id === b.id}
        urlBuilderFn={(item) => item.content?.slug ?? ""}
        useQueryFn={useGetProjectsQuery}
        onUrlStateChange={trackComparisonDone}
      >
        <SearchProjectsHeader>
          <SearchProjectsHeaderContent>
            <SearchProjectsHeaderTitle>
              {t("search_projects_page.started.header.title")}
            </SearchProjectsHeaderTitle>
          </SearchProjectsHeaderContent>
          <SearchProjectsHeaderContent>
            <SearchProjectsRefinementsMenuTrigger />
            <SearchProjectsRefinements
              className="hidden w-3/4 md:flex"
              onRefineItemChange={(refinementList, item, checked) => {
                if (checked) {
                  trackRefinement(refinementList, item);
                }
              }}
            />
            <ProjectsComparatorActions />
          </SearchProjectsHeaderContent>
        </SearchProjectsHeader>

        <SearchProjectsRefinementsMenu />

        <main className="bg-black-400">
          <StartedProjectsContent />
        </main>

        <section className="bg-black-800">
          <TrackRecordBlock />
        </section>

        <div className="container mx-auto w-full">
          <AppointmentCardNew className="p-4 lg:p-16" />
        </div>

        <ProjectsComparatorSelectionBanner />
        <ProjectsComparatorModal />
      </ComparatorProvider>
    </SearchProjectsProvider>
  );
}
